import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtEuro } from "@/lib/format";
import { PageHeader } from "@/components/Common";
import { CrudSection } from "@/components/CrudSection";
import { StockTab } from "@/components/warehouse/StockTab";
import { MovementsTab } from "@/components/warehouse/MovementsTab";
import { SerialsTab } from "@/components/warehouse/SerialsTab";
import { PurchaseOrdersTab } from "@/components/warehouse/PurchaseOrders";
import { VanRefillsTab } from "@/components/warehouse/VanRefills";
import { ReturnsTab } from "@/components/warehouse/Returns";
import { CountsTab } from "@/components/warehouse/CountsTab";
import { PriceListTab } from "@/components/warehouse/PriceListTab";
import { ReceiptTab } from "@/components/warehouse/ReceiptTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DdtTab } from "@/components/warehouse/DdtTab";
import { ItemsReplace } from "@/components/warehouse/ItemsReplace";
import { WarehouseWipe } from "@/components/warehouse/WarehouseWipe";

const baseItemFields = [
  { key: "code", label: "Codice" }, { key: "name", label: "Descrizione" }, { key: "unit", label: "Unità di misura", placeholder: "pz, m, conf" },
  { key: "category", label: "Categoria" }, { key: "barcode", label: "Codice a barre (EAN)", optional: true }, { key: "serialized", label: "Gestito a seriale", type: "switch" },
  { key: "min_stock", label: "Scorta minima (magazzini centrali)", type: "number" }, { key: "unit_cost", label: "Costo unitario", type: "number" },
  { key: "sale_price", label: "Prezzo di vendita", type: "number" },
  { key: "active", label: "Attivo", type: "switch" },
];
const itemCols = [
  { label: "Codice", render: (i) => <span className="font-mono text-xs font-semibold">{i.code}</span> }, { label: "Descrizione", render: (i) => i.name },
  { label: "Categoria", render: (i) => i.category }, { label: "U.M.", render: (i) => i.unit }, { label: "Seriale", render: (i) => (i.serialized ? "Sì" : "No") },
  { label: "Scorta min.", render: (i) => i.min_stock }, { label: "Riordino", render: (i) => i.reorder_qty || "—" }, { label: "Dotaz. furgone", render: (i) => i.van_standard_qty || "—" },
  { label: "Costo", render: (i) => <span className="font-mono">{fmtEuro(i.unit_cost)}</span> },
  { label: "Vendita", render: (i) => <span className="font-mono">{i.sale_price ? fmtEuro(i.sale_price) : "—"}</span> },
  { label: "Stato", render: (i) => (i.active ? "Attivo" : "Disattivo") },
];

export default function Warehouse() {
  const { can, user } = useAuth();
  const { companies, visible_company_ids, teams, nameOf, parties, inCompany } = useData();
  const [items, setItems] = useState([]);
  const [allWarehouses, setWarehouses] = useState([]);
  const warehouses = allWarehouses.filter((w) => inCompany(w.company_id));
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState("stock");
  const [serialPreset, setSerialPreset] = useState(null);
  const openSerials = (p) => { setSerialPreset({ ...p, at: Date.now() }); setTab("serials"); };
  const load = useCallback(() => {
    api.get("/warehouse/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/warehouse/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); setRefreshKey((k) => k + 1); };
  const own = companies.filter((c) => visible_company_ids.includes(c.id)).map((c) => ({ value: c.id, label: c.legal_name }));
  const itemFields = [...baseItemFields,
    { key: "supplier_id", label: "Fornitore predefinito", type: "select", optional: true, options: parties.filter((p) => p.roles.includes("supplier")).map((p) => ({ value: p.id, label: p.name })) },
    { key: "reorder_qty", label: "Quantità di riordino", type: "number" }, { key: "van_standard_qty", label: "Dotazione standard per furgone", type: "number" }];
  const whFields = [
    { key: "name", label: "Nome" }, { key: "kind", label: "Tipo", type: "select", options: [{ value: "CENTRAL", label: "Magazzino centrale" }, { value: "VAN", label: "Furgone squadra" }] },
    { key: "company_id", label: "Società", type: "select", options: own },
    { key: "team_id", label: "Squadra (solo furgoni)", type: "select", optional: true, options: teams.map((t) => ({ value: t.id, label: t.name })) },
    { key: "active", label: "Attivo", type: "switch" },
  ];
  const whCols = [
    { label: "Nome", render: (w) => <span className="font-semibold">{w.name}</span> }, { label: "Tipo", render: (w) => (w.kind === "VAN" ? "Furgone" : "Centrale") },
    { label: "Società", render: (w) => nameOf("companies", w.company_id, "code") }, { label: "Squadra", render: (w) => (w.team_id ? nameOf("teams", w.team_id) : "—") },
  ];
  const write = can("inventory:write");
  return (
    <div data-testid="warehouse-page">
      <PageHeader eyebrow="Logistica" title="Magazzino">
        <div className="font-mono text-sm" data-testid="warehouse-counts">
          <b data-testid="warehouse-count-central">{warehouses.filter((w) => w.active !== false && w.kind === "CENTRAL").length}</b> magazzini centrali · <b data-testid="warehouse-count-vans">{warehouses.filter((w) => w.active !== false && w.kind === "VAN").length}</b> furgoni
        </div>
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 h-auto flex-wrap">
          <TabsTrigger value="stock" data-testid="warehouse-tab-stock">Giacenze</TabsTrigger>
          {write && <TabsTrigger value="receipt" data-testid="warehouse-tab-receipt">Carico merce</TabsTrigger>}
          <TabsTrigger value="movements" data-testid="warehouse-tab-movements">Movimenti</TabsTrigger>
          <TabsTrigger value="serials" data-testid="warehouse-tab-serials">Seriali</TabsTrigger>
          <TabsTrigger value="orders" data-testid="warehouse-tab-orders">Riordini fornitori</TabsTrigger>
          <TabsTrigger value="refills" data-testid="warehouse-tab-refills">Reintegri e richieste tecnici</TabsTrigger>
          <TabsTrigger value="ddt" data-testid="warehouse-tab-ddt">DDT</TabsTrigger>
          <TabsTrigger value="returns" data-testid="warehouse-tab-returns">Resi e rientri</TabsTrigger>
          <TabsTrigger value="counts" data-testid="warehouse-tab-counts">Inventari</TabsTrigger>
          <TabsTrigger value="items" data-testid="warehouse-tab-items">Articoli</TabsTrigger>
          <TabsTrigger value="pricelist" data-testid="warehouse-tab-pricelist">Listino acquisto/vendita</TabsTrigger>
          <TabsTrigger value="warehouses" data-testid="warehouse-tab-warehouses">Magazzini e furgoni</TabsTrigger>
        </TabsList>
        <TabsContent value="stock"><StockTab warehouses={warehouses} refreshKey={refreshKey} onOpenSerials={openSerials} /></TabsContent>
        {write && <TabsContent value="receipt"><ReceiptTab items={items} warehouses={warehouses} onChanged={changed} /></TabsContent>}
        <TabsContent value="movements"><MovementsTab items={items} warehouses={warehouses} onChanged={changed} /></TabsContent>
        <TabsContent value="serials"><SerialsTab warehouses={warehouses} refreshKey={refreshKey} canWrite={write} preset={serialPreset} /></TabsContent>
        <TabsContent value="orders"><PurchaseOrdersTab onChanged={changed} /></TabsContent>
        <TabsContent value="refills"><VanRefillsTab onChanged={changed} /></TabsContent>
        <TabsContent value="ddt"><DdtTab /></TabsContent>
        <TabsContent value="returns"><ReturnsTab warehouses={warehouses} onChanged={changed} /></TabsContent>
        <TabsContent value="counts"><CountsTab warehouses={warehouses} onChanged={changed} /></TabsContent>
        <TabsContent value="pricelist"><PriceListTab onChanged={changed} /></TabsContent>
        <TabsContent value="items">
          {user?.role === "admin" && <div className="mb-3 flex flex-wrap justify-end gap-2"><WarehouseWipe onDone={changed} /><ItemsReplace onDone={changed} /></div>}
          <CrudSection key={refreshKey} endpoint="warehouse/items" title="Articoli" fields={itemFields} columns={itemCols} testPrefix="items" canWrite={write} onChange={changed}
            defaults={{ code: "", name: "", unit: "pz", category: "", serialized: false, min_stock: 0, unit_cost: 0, sale_price: 0, active: true, supplier_id: null, reorder_qty: 0, van_standard_qty: 0, barcode: "" }} />
        </TabsContent>
        <TabsContent value="warehouses">
          <CrudSection endpoint="warehouse/warehouses" title="Magazzini e furgoni" fields={whFields} columns={whCols} testPrefix="warehouses" filter={(w) => inCompany(w.company_id)} canWrite={write} onChange={changed}
            defaults={{ name: "", kind: "CENTRAL", company_id: "", team_id: null, active: true }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
